import { FC, createContext, useContext, useEffect, useState } from 'react';
import { WithChildren } from 'ts-types';

export interface PageLink {
  title: string;
  path: string;
  isActive: boolean;
  isSeparator?: boolean;
}

export interface PageDataContextModel {
  pageTitle?: string;
  setPageTitle: (_title: string) => void;
  pageDescription?: string;
  setPageDescription: (_description: string) => void;
  pageBreadcrumbs?: Array<PageLink>;
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => void;
  pageTitleBreadcrumb?: string;
  setPageTitleBreadcrumb: (_title: string) => void;
}

const PageDataContext = createContext<PageDataContextModel>({
  setPageTitle: (_title: string) => {
    // empty
  },
  setPageBreadcrumbs: (_breadcrumbs: Array<PageLink>) => {
    // empty
  },
  setPageDescription: (_description: string) => {
    // empty
  },
  setPageTitleBreadcrumb: (_title: string) => {
    // empty
  }
});

const PageDataProvider: FC<WithChildren> = ({ children }) => {
  const [pageTitle, setPageTitle] = useState<string>('');
  const [pageTitleBreadcrumb, setPageTitleBreadcrumb] = useState<string>('');
  const [pageDescription, setPageDescription] = useState<string>('');
  const [pageBreadcrumbs, setPageBreadcrumbs] = useState<Array<PageLink>>([]);
  const value: PageDataContextModel = {
    pageTitle,
    setPageTitle,
    pageDescription,
    setPageDescription,
    pageBreadcrumbs,
    setPageBreadcrumbs,
    pageTitleBreadcrumb,
    setPageTitleBreadcrumb
  };
  return <PageDataContext.Provider value={value}>{children}</PageDataContext.Provider>;
};

function usePageData() {
  return useContext(PageDataContext);
}

type Props = {
  description?: string;
  breadcrumbs?: Array<PageLink>;
  pageTitleBreadcrumb?: string;
};

const PageTitle: FC<Props & WithChildren> = ({ children, description, breadcrumbs, pageTitleBreadcrumb }) => {
  const { setPageTitle, setPageDescription, setPageBreadcrumbs, setPageTitleBreadcrumb } = usePageData();
  useEffect(() => {
    if (children) {
      setPageTitle(children.toString());
    }
    return () => {
      setPageTitle('');
    };
  }, [children]);

  useEffect(() => {
    if (description) {
      setPageDescription(description);
    }
    return () => {
      setPageDescription('');
    };
  }, [description]);

  useEffect(() => {
    if (breadcrumbs) {
      setPageBreadcrumbs(breadcrumbs);
    }
    return () => {
      setPageBreadcrumbs([]);
    };
  }, [breadcrumbs]);

  useEffect(() => {
    if (pageTitleBreadcrumb) {
      setPageTitleBreadcrumb(pageTitleBreadcrumb);
    }
    return () => {
      setPageTitleBreadcrumb('');
    };
  }, [pageTitleBreadcrumb]);

  return <></>;
};

const PageDescription: FC<WithChildren> = ({ children }) => {
  const { setPageDescription } = usePageData();
  useEffect(() => {
    if (children) {
      setPageDescription(children.toString());
    }
    return () => {
      setPageDescription('');
    };
  }, [children]);
  return <></>;
};

export { PageDescription, PageTitle, PageDataProvider, usePageData };
